/**
 * GoogleMapPicker
 * ─────────────────────────────────────────────
 * • Shows a Google Map with a draggable pin
 * • Places Autocomplete search box (Arabic)
 * • GPS button
 * • Calls onPick({ lat, lng, address }) on every pin change
 */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import useGoogleMaps from '../hooks/useGoogleMaps';

const DEFAULT_CENTER = { lat: 24.7136, lng: 46.6753 };

export default function GoogleMapPicker({ initialPos, onPick, height = 300 }) {
  const { loaded, error } = useGoogleMaps();
  const mapRef      = useRef(null);
  const mapObj      = useRef(null);
  const markerRef   = useRef(null);
  const inputRef    = useRef(null);
  const acRef       = useRef(null);
  const [gpsLoading, setGpsLoading] = useState(false);

  /* ── reverse geocode via Google ── */
  const reverseGeocode = useCallback((lat, lng) => {
    const gc = new window.google.maps.Geocoder();
    gc.geocode({ location: { lat, lng }, language: 'ar' }, (results, status) => {
      const address = (status === 'OK' && results[0])
        ? results[0].formatted_address
        : `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
      onPick({ lat, lng, address });
    });
  }, [onPick]);

  /* ── place / move marker ── */
  const placeMarker = useCallback((lat, lng) => {
    if (!mapObj.current) return;
    const pos = { lat, lng };
    if (!markerRef.current) {
      markerRef.current = new window.google.maps.Marker({
        position: pos,
        map: mapObj.current,
        draggable: true,
        animation: window.google.maps.Animation.DROP,
        icon: {
          path: window.google.maps.SymbolPath.CIRCLE,
          scale: 10,
          fillColor: '#0ABAB5',
          fillOpacity: 1,
          strokeColor: '#ffffff',
          strokeWeight: 2.5,
        },
      });
      markerRef.current.addListener('dragend', e => {
        reverseGeocode(e.latLng.lat(), e.latLng.lng());
      });
    } else {
      markerRef.current.setPosition(pos);
    }
    mapObj.current.panTo(pos);
    mapObj.current.setZoom(15);
    reverseGeocode(lat, lng);
  }, [reverseGeocode]);

  /* ── init map ── */
  useEffect(() => {
    if (!loaded || !mapRef.current || mapObj.current) return;

    const center = initialPos
      ? { lat: initialPos[0], lng: initialPos[1] }
      : DEFAULT_CENTER;

    mapObj.current = new window.google.maps.Map(mapRef.current, {
      center,
      zoom: initialPos ? 15 : 11,
      disableDefaultUI: true,
      zoomControl: true,
      zoomControlOptions: {
        position: window.google.maps.ControlPosition.LEFT_BOTTOM,
      },
      clickableIcons: false,
      styles: [
        { featureType: 'poi', elementType: 'labels', stylers: [{ visibility: 'off' }] },
      ],
    });

    mapObj.current.addListener('click', e => {
      placeMarker(e.latLng.lat(), e.latLng.lng());
    });

    if (initialPos) placeMarker(initialPos[0], initialPos[1]);
  }, [loaded, initialPos, placeMarker]);

  /* ── Autocomplete ── */
  useEffect(() => {
    if (!loaded || !inputRef.current || acRef.current) return;
    acRef.current = new window.google.maps.places.Autocomplete(inputRef.current, {
      componentRestrictions: { country: 'sa' },
      fields: ['geometry', 'formatted_address'],
      language: 'ar',
    });
    acRef.current.addListener('place_changed', () => {
      const place = acRef.current.getPlace();
      if (!place.geometry) return;
      const lat = place.geometry.location.lat();
      const lng = place.geometry.location.lng();
      placeMarker(lat, lng);
    });
  }, [loaded, placeMarker]);

  /* ── GPS ── */
  const getGPS = () => {
    if (!navigator.geolocation) return;
    setGpsLoading(true);
    navigator.geolocation.getCurrentPosition(
      pos => {
        setGpsLoading(false);
        placeMarker(pos.coords.latitude, pos.coords.longitude);
      },
      () => setGpsLoading(false),
      { enableHighAccuracy: true, timeout: 8000 },
    );
  };

  if (error) return (
    <div style={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'var(--wt-surface)', borderRadius: '1rem', border: '1px solid var(--wt-border)' }}>
      <p style={{ color: 'var(--wt-text-muted)', fontSize: '0.8rem' }}>تعذّر تحميل الخريطة</p>
    </div>
  );

  if (!loaded) return (
    <div style={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'var(--wt-surface)', borderRadius: '1rem', border: '1px solid var(--wt-border)' }}>
      <div style={{ width: 28, height: 28, borderRadius: '50%',
        border: '3px solid rgba(10,186,181,0.2)', borderTopColor: '#0ABAB5',
        animation: 'spin 0.7s linear infinite' }} />
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );

  return (
    <div style={{ position: 'relative', borderRadius: '1rem', overflow: 'hidden',
      border: '1px solid var(--wt-border)', height }}>

      {/* Search box */}
      <div style={{ position: 'absolute', top: 10, right: 10, left: 10, zIndex: 10 }}>
        <div style={{ position: 'relative' }}>
          <svg style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
            width: 16, height: 16, color: '#0ABAB5', pointerEvents: 'none' }}
            viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          <input
            ref={inputRef}
            type="text"
            placeholder="ابحث عن موقع..."
            style={{
              width: '100%', padding: '0.55rem 2.2rem 0.55rem 0.75rem',
              borderRadius: '0.65rem', border: '1px solid rgba(10,186,181,0.3)',
              background: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(8px)',
              fontSize: '0.8rem', fontFamily: "'Cairo', sans-serif",
              direction: 'rtl', outline: 'none', boxSizing: 'border-box',
              boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
            }}
          />
        </div>
      </div>

      {/* Map container */}
      <div ref={mapRef} style={{ width: '100%', height: '100%' }} />

      {/* GPS button */}
      <button
        onClick={getGPS}
        disabled={gpsLoading}
        style={{
          position: 'absolute', bottom: 12, left: 12, zIndex: 10,
          display: 'flex', alignItems: 'center', gap: '0.35rem',
          padding: '0.45rem 0.75rem', borderRadius: '0.65rem',
          background: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(8px)',
          border: '1px solid rgba(10,186,181,0.3)', cursor: gpsLoading ? 'wait' : 'pointer',
          fontSize: '0.75rem', fontFamily: "'Cairo', sans-serif",
          color: '#0ABAB5', fontWeight: 700, boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
        }}
      >
        {gpsLoading
          ? <><span style={{ width: 12, height: 12, borderRadius: '50%',
              border: '2px solid rgba(10,186,181,0.3)', borderTopColor: '#0ABAB5',
              animation: 'spin 0.7s linear infinite', display: 'inline-block' }} />جاري...</>
          : <><svg viewBox="0 0 24 24" width="14" height="14" fill="none"
              stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <circle cx="12" cy="12" r="3"/><path d="M12 2v3M12 19v3M2 12h3M19 12h3"/>
              <path d="M12 1v2M12 21v2M1 12h2M21 12h2"/>
            </svg>موقعي</>
        }
      </button>
    </div>
  );
}
